import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { getUserById, suspendUser, unsuspendUser, approveUnsuspensionRequest, rejectUnsuspensionRequest } from '../../../api/services/adminService';
import { fetchAuthenticatedImage } from '../../../api/services/authService';
import './UserDetailsModal.css';

const UserDetailsModal = ({ userId, unsuspensionRequestId, isOpen, onClose }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [suspending, setSuspending] = useState(false);
  const [unsuspending, setUnsuspending] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);
  const [showUnsuspendConfirm, setShowUnsuspendConfirm] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [suspendError, setSuspendError] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [allowUnsuspensionRequest, setAllowUnsuspensionRequest] = useState(true);
  const [unsuspendError, setUnsuspendError] = useState(null);
  const [approveError, setApproveError] = useState(null);
  const [rejectError, setRejectError] = useState(null);
  const blobUrlRef = useRef(null);

  const loadUserDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getUserById(userId);
      
      if (response.status === 'success' && response.data) {
        setUser(response.data);
      } else {
        setError('Failed to load user details');
      }
    } catch (err) {
      setError(err.message || 'Failed to load user details');
      console.error('Error loading user details:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen && userId) {
      loadUserDetails();
    } else {
      // Reset state when modal closes
      setUser(null);
      setError(null);
      setProfileImageUrl(null);
    }

    // Cleanup blob URL on unmount
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [isOpen, userId, loadUserDetails]);

  // Load profile image when user data is available
  useEffect(() => {
    const loadProfileImage = async () => {
      // Clean up previous blob URL if it exists
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }

      if (!user?.profilePhotoPath) {
        setProfileImageUrl(null);
        return;
      }

      // If it's already a data URL or blob URL, use it directly
      if (user.profilePhotoPath.startsWith('data:') || user.profilePhotoPath.startsWith('blob:')) {
        setProfileImageUrl(user.profilePhotoPath);
        return;
      }

      // Fetch authenticated image
      try {
        const blobUrl = await fetchAuthenticatedImage(user.profilePhotoPath);
        if (blobUrl) {
          blobUrlRef.current = blobUrl;
          setProfileImageUrl(blobUrl);
        } else {
          setProfileImageUrl(null);
        }
      } catch (error) {
        console.error('Failed to load authenticated image:', error);
        setProfileImageUrl(null);
      }
    };

    if (user) {
      loadProfileImage();
    }
  }, [user]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  const getUserRoles = (roles) => {
    if (!roles || roles.length === 0) return 'N/A';
    return roles.join(', ');
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const getVerificationBadge = (status) => {
    if (!status || status === 'NONE') return <span className="badge-none">Not Verified</span>;
    if (status === 'APPROVED') return <span className="badge-approved">Verified</span>;
    return <span className="badge-pending">Pending</span>;
  };

  const handleSuspendClick = () => {
    setShowSuspendConfirm(true);
    setSuspendError(null);
    setSuspendReason('');
    setAllowUnsuspensionRequest(true);
  };

  const handleSuspendConfirm = async () => {
    const trimmedReason = suspendReason.trim();
    if (!trimmedReason) {
      setSuspendError('Please provide a reason for suspension');
      return;
    }

    try {
      setSuspending(true);
      setSuspendError(null);
      const response = await suspendUser(userId, {
        reason: trimmedReason,
        allowUnsuspensionRequest,
      });
      
      if (response.status === 'success') {
        // Reload user data to get updated status
        await loadUserDetails();
        setShowSuspendConfirm(false);
      } else {
        setSuspendError('Failed to suspend user');
      }
    } catch (err) {
      setSuspendError(err.message || 'Failed to suspend user');
      console.error('Error suspending user:', err);
    } finally {
      setSuspending(false);
    }
  };

  const handleSuspendCancel = () => {
    setShowSuspendConfirm(false);
    setSuspendError(null);
    setSuspendReason('');
    setAllowUnsuspensionRequest(true);
  };

  const handleUnsuspendClick = () => {
    setShowUnsuspendConfirm(true);
    setUnsuspendError(null);
  };

  const handleUnsuspendConfirm = async () => {
    try {
      setUnsuspending(true);
      setUnsuspendError(null);
      const response = await unsuspendUser(userId);
      
      if (response.status === 'success') {
        // Reload user data to get updated status
        await loadUserDetails();
        setShowUnsuspendConfirm(false);
      } else {
        setUnsuspendError('Failed to unsuspend user');
      }
    } catch (err) {
      setUnsuspendError(err.message || 'Failed to unsuspend user');
      console.error('Error unsuspending user:', err);
    } finally {
      setUnsuspending(false);
    }
  };

  const handleUnsuspendCancel = () => {
    setShowUnsuspendConfirm(false);
    setUnsuspendError(null);
  };

  const handleApproveClick = () => {
    setShowApproveConfirm(true);
    setApproveError(null);
  };

  const handleApproveConfirm = async () => {
    try {
      setApproving(true);
      setApproveError(null);
      const response = await approveUnsuspensionRequest(unsuspensionRequestId);
      
      if (response.status === 'success') {
        // Reload user data to get updated status
        await loadUserDetails();
        setShowApproveConfirm(false);
        // Optionally close modal or show success message
      } else {
        setApproveError('Failed to approve unsuspension request');
      }
    } catch (err) {
      setApproveError(err.message || 'Failed to approve unsuspension request');
      console.error('Error approving unsuspension request:', err);
    } finally {
      setApproving(false);
    }
  };

  const handleApproveCancel = () => {
    setShowApproveConfirm(false);
    setApproveError(null);
  };

  const handleRejectClick = () => {
    setShowRejectConfirm(true);
    setRejectError(null);
  };

  const handleRejectConfirm = async () => {
    try {
      setRejecting(true);
      setRejectError(null);
      const response = await rejectUnsuspensionRequest(unsuspensionRequestId);
      
      if (response.status === 'success') {
        // Reload user data to get updated status
        await loadUserDetails();
        setShowRejectConfirm(false);
        // Optionally close modal or show success message
      } else {
        setRejectError('Failed to reject unsuspension request');
      }
    } catch (err) {
      setRejectError(err.message || 'Failed to reject unsuspension request');
      console.error('Error rejecting unsuspension request:', err);
    } finally {
      setRejecting(false);
    }
  };

  const handleRejectCancel = () => {
    setShowRejectConfirm(false);
    setRejectError(null);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">User Details</h2>
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="modal-loading">
              <p>Loading user details...</p>
            </div>
          )}

          {error && (
            <div className="modal-error">
              <p>Error: {error}</p>
              <button onClick={loadUserDetails} className="retry-button">Retry</button>
            </div>
          )}

          {!loading && !error && user && (
            <div className="user-details">
              {/* Profile Header Section */}
              <div className="user-details-header">
                <div className="user-details-photo-section">
                  {profileImageUrl ? (
                    <img 
                      src={profileImageUrl} 
                      alt={`${user.firstName} ${user.lastName}`}
                      className="user-details-photo"
                    />
                  ) : (
                    <div className="user-details-photo-placeholder">
                      <span>{`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || '?'}</span>
                    </div>
                  )}
                </div>
                <div className="user-details-header-info">
                  <h3 className="user-details-name">{user.firstName} {user.lastName}</h3>
                  <div className="user-details-status-badges">
                    {getStatusBadge(user.isActive)}
                    {user.isVerified && <span className="badge-verified">Verified</span>}
                  </div>
                </div>
              </div>

              {/* Personal Information Section */}
              <div className="user-details-section">
                <h4 className="section-title">Personal Information</h4>
                <div className="section-content">
                  <div className="detail-row">
                    <span className="detail-label">Email Address</span>
                    <span className="detail-value">{user.email}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Phone Number</span>
                    <span className="detail-value">{user.phone || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">ID Number</span>
                    <span className="detail-value">{user.idNumber || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Date of Birth</span>
                    <span className="detail-value">{user.dateOfBirth || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Account Information Section */}
              <div className="user-details-section">
                <h4 className="section-title">Account Information</h4>
                <div className="section-content">
                  <div className="detail-row">
                    <span className="detail-label">Roles</span>
                    <span className="detail-value roles-value">{getUserRoles(user.roles)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Registration Source</span>
                    <span className="detail-value">{user.registrationSource || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Referral Code</span>
                    <span className="detail-value">{user.referralCode || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Verification Status Section */}
              <div className="user-details-section">
                <h4 className="section-title">Verification Status</h4>
                <div className="section-content">
                  <div className="detail-row">
                    <span className="detail-label">KYC Status</span>
                    <span className="detail-value">{getVerificationBadge(user.kycStatus)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">KYB Status</span>
                    <span className="detail-value">{getVerificationBadge(user.kybStatus)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Has KYB</span>
                    <span className="detail-value">{user.hasKyb ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Phone Verified</span>
                    <span className="detail-value">{user.phoneVerifiedAt ? formatDate(user.phoneVerifiedAt) : 'Not Verified'}</span>
                  </div>
                </div>
              </div>

              {/* Activity & Timestamps Section */}
              <div className="user-details-section">
                <h4 className="section-title">Activity & Timestamps</h4>
                <div className="section-content">
                  <div className="detail-row">
                    <span className="detail-label">Last Login</span>
                    <span className="detail-value">{formatDate(user.lastLogin)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Account Created</span>
                    <span className="detail-value">{formatDate(user.createdAt)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Last Updated</span>
                    <span className="detail-value">{formatDate(user.updatedAt)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {!loading && !error && user && (
            <>
              {!showSuspendConfirm && !showUnsuspendConfirm && !showApproveConfirm && !showRejectConfirm ? (
                <>
                  {unsuspensionRequestId ? (
                    // Show Approve and Reject buttons when viewing from unsuspension requests
                    <>
                      <button 
                        className="modal-approve-button" 
                        onClick={handleApproveClick}
                        disabled={approving || rejecting || suspending || unsuspending}
                      >
                        Approve Unsuspension
                      </button>
                      <button 
                        className="modal-reject-button" 
                        onClick={handleRejectClick}
                        disabled={approving || rejecting || suspending || unsuspending}
                      >
                        Reject Unsuspension
                      </button>
                    </>
                  ) : user.isActive ? (
                    <button 
                      className="modal-suspend-button" 
                      onClick={handleSuspendClick}
                      disabled={suspending || unsuspending || approving || rejecting}
                    >
                      Suspend User
                    </button>
                  ) : (
                    <button 
                      className="modal-unsuspend-button" 
                      onClick={handleUnsuspendClick}
                      disabled={suspending || unsuspending || approving || rejecting}
                    >
                      Unsuspend User
                    </button>
                  )}
                  <button className="modal-close-button" onClick={onClose}>
                    Close
                  </button>
                </>
              ) : showSuspendConfirm ? (
                <div className="suspend-confirmation">
                  <p className="suspend-confirm-message">
                    Suspend {user.firstName} {user.lastName}
                  </p>
                  <div className="suspend-form-field">
                    <label htmlFor="suspend-reason" className="suspend-form-label">
                      Reason for suspension <span className="suspend-required">*</span>
                    </label>
                    <textarea
                      id="suspend-reason"
                      className="suspend-reason-input"
                      value={suspendReason}
                      onChange={(e) => setSuspendReason(e.target.value)}
                      placeholder="e.g. Violation of terms of service"
                      rows={3}
                      disabled={suspending}
                    />
                  </div>
                  <label className="suspend-checkbox-label">
                    <input
                      type="checkbox"
                      checked={allowUnsuspensionRequest}
                      onChange={(e) => setAllowUnsuspensionRequest(e.target.checked)}
                      disabled={suspending}
                    />
                    Allow user to request unsuspension
                  </label>
                  {suspendError && (
                    <p className="suspend-error-message">{suspendError}</p>
                  )}
                  <div className="suspend-confirm-buttons">
                    <button 
                      className="modal-cancel-button" 
                      onClick={handleSuspendCancel}
                      disabled={suspending}
                    >
                      Cancel
                    </button>
                    <button 
                      className="modal-suspend-confirm-button" 
                      onClick={handleSuspendConfirm}
                      disabled={suspending || !suspendReason.trim()}
                    >
                      {suspending ? 'Suspending...' : 'Confirm Suspend'}
                    </button>
                  </div>
                </div>
              ) : showUnsuspendConfirm ? (
                <div className="suspend-confirmation">
                  <p className="suspend-confirm-message">
                    Are you sure you want to unsuspend {user.firstName} {user.lastName}?
                  </p>
                  {unsuspendError && (
                    <p className="suspend-error-message">{unsuspendError}</p>
                  )}
                  <div className="suspend-confirm-buttons">
                    <button 
                      className="modal-cancel-button" 
                      onClick={handleUnsuspendCancel}
                      disabled={unsuspending}
                    >
                      Cancel
                    </button>
                    <button 
                      className="modal-unsuspend-confirm-button" 
                      onClick={handleUnsuspendConfirm}
                      disabled={unsuspending}
                    >
                      {unsuspending ? 'Unsuspending...' : 'Confirm Unsuspend'}
                    </button>
                  </div>
                </div>
              ) : showApproveConfirm ? (
                <div className="suspend-confirmation">
                  <p className="suspend-confirm-message">
                    Are you sure you want to approve the unsuspension request for {user.firstName} {user.lastName}?
                  </p>
                  {approveError && (
                    <p className="suspend-error-message">{approveError}</p>
                  )}
                  <div className="suspend-confirm-buttons">
                    <button 
                      className="modal-cancel-button" 
                      onClick={handleApproveCancel}
                      disabled={approving}
                    >
                      Cancel
                    </button>
                    <button 
                      className="modal-approve-confirm-button" 
                      onClick={handleApproveConfirm}
                      disabled={approving}
                    >
                      {approving ? 'Approving...' : 'Confirm Approve'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="suspend-confirmation">
                  <p className="suspend-confirm-message">
                    Are you sure you want to reject the unsuspension request for {user.firstName} {user.lastName}?
                  </p>
                  {rejectError && (
                    <p className="suspend-error-message">{rejectError}</p>
                  )}
                  <div className="suspend-confirm-buttons">
                    <button 
                      className="modal-cancel-button" 
                      onClick={handleRejectCancel}
                      disabled={rejecting}
                    >
                      Cancel
                    </button>
                    <button 
                      className="modal-reject-confirm-button" 
                      onClick={handleRejectConfirm}
                      disabled={rejecting}
                    >
                      {rejecting ? 'Rejecting...' : 'Confirm Reject'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          {(!user || loading || error) && (
            <button className="modal-close-button" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UserDetailsModal;
